import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';

type Granularity = 'day' | 'week' | 'month';

interface DayPoint {
  date: string;
  import_on_gwitrace: number;
}

interface RolloutRow {
  name: string;
  covers: string;
  codes: string[];
  category: 'Rail' | 'SS Line' | 'Terminal';
  go_live: string | null;
  status: 'live' | 'upcoming' | 'tbd';
  trackable: number;
  on_gwitrace: number;
}

interface CategoryProgress {
  category: string;
  trackable: number;
  on_gwitrace: number;
  deferred: number;        // in-house-tracked, not headed to gwiTrace soon
  pct: number;
  deferred_pct: number;
}

interface ProgressResponse {
  start_date: string;
  end_date: string;
  gwitrace_go_live: string;
  open_totals: { trackable: number; on_gwitrace: number };
  series: DayPoint[];
  rollout: RolloutRow[];
  category_progress: CategoryProgress[];
  tracers_live: number;
  tracers_total: number;
}

interface ChartPoint {
  label: string;
  bucketStart: string;
  import_on_gwitrace: number;
  cumulative: number; // running total of containers on gwiTrace through this bucket
}

const ADOPTION_COLOR = '#10b981';

const parseDate = (s: string): Date => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
};
const weekStart = (d: Date): Date => {
  const out = new Date(d);
  out.setDate(out.getDate() - ((out.getDay() + 6) % 7));
  return out;
};
const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtShort = (d: Date): string => `${d.getMonth() + 1}/${d.getDate()}`;
const fmtMonth = (d: Date): string => d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
const nfmt = (n: number): string => n.toLocaleString('en-US');

const rollup = (series: DayPoint[], granularity: Granularity): ChartPoint[] => {
  const buckets = new Map<string, ChartPoint>();
  for (const p of series) {
    const d = parseDate(p.date);
    let key: string, label: string, bucketStart: string;
    if (granularity === 'day') {
      key = p.date; label = fmtShort(d); bucketStart = p.date;
    } else if (granularity === 'week') {
      const ws = weekStart(d); key = iso(ws); label = fmtShort(ws); bucketStart = key;
    } else {
      key = p.date.slice(0, 7); label = fmtMonth(d); bucketStart = `${key}-01`;
    }
    let b = buckets.get(key);
    if (!b) { b = { label, bucketStart, import_on_gwitrace: 0, cumulative: 0 }; buckets.set(key, b); }
    b.import_on_gwitrace += p.import_on_gwitrace;
  }
  const rows = Array.from(buckets.entries())
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([, b]) => b);
  // Running total: total containers on gwiTrace created through each bucket.
  let cum = 0;
  for (const b of rows) {
    cum += b.import_on_gwitrace;
    b.cumulative = cum;
  }
  return rows;
};

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-2xl font-semibold" style={accent ? { color: accent } : undefined}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: RolloutRow['status'] }) {
  const map: Record<RolloutRow['status'], { text: string; cls: string }> = {
    live: { text: 'Live', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
    upcoming: { text: 'Upcoming', cls: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
    tbd: { text: 'Deferred', cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  };
  const { text, cls } = map[status];
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{text}</span>;
}

function GwitraceProgress() {
  const [data, setData] = useState<ProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<Granularity>('week');

  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; abortControllerRef.current?.abort(); };
  }, []);

  const fetchData = useCallback(async () => {
    if (!isMounted.current) return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      setError(null);
      setLoading(true);
      const response = await api.get('/reporting/gwitrace-progress', { signal: controller.signal });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
      setError(null);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      if (isMounted.current && !controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const chartData = useMemo<ChartPoint[]>(() => (data ? rollup(data.series, granularity) : []), [data, granularity]);

  const stats = useMemo(() => {
    if (!data) return null;
    const { trackable, on_gwitrace } = data.open_totals;
    const reach = trackable > 0 ? (on_gwitrace / trackable) * 100 : 0;
    const cumulativeTotal = data.series.reduce((a, p) => a + p.import_on_gwitrace, 0);
    return { trackable, on_gwitrace, reach, cumulativeTotal };
  }, [data]);

  // Group the rollout by category (preserving first-seen order), and within each
  // section drop the deferred (in-house) tracers to the bottom. Array.sort is
  // stable, so non-deferred keep their plan order.
  const groupedRollout = useMemo(() => {
    if (!data) return [] as { category: string; rows: RolloutRow[] }[];
    const order: string[] = [];
    const map = new Map<string, RolloutRow[]>();
    for (const r of data.rollout ?? []) {
      if (!map.has(r.category)) {
        map.set(r.category, []);
        order.push(r.category);
      }
      map.get(r.category)!.push(r);
    }
    return order.map((category) => ({
      category,
      rows: [...map.get(category)!].sort(
        (a, b) => (a.status === 'tbd' ? 1 : 0) - (b.status === 'tbd' ? 1 : 0)
      ),
    }));
  }, [data]);

  const granularityOptions: { key: Granularity; label: string }[] = [
    { key: 'day', label: 'Daily' },
    { key: 'week', label: 'Weekly' },
    { key: 'month', label: 'Monthly' },
  ];

  const infoBanner = (
    <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      How the gwiTrace rollout is progressing toward 100% of import containers, tracer by tracer
      (rail carrier, ocean line, or terminal). Percentages count <strong>trackable</strong> containers
      only — those with a container # and BOL that haven't been outgated or trace-terminated — and a
      carrier is credited once it goes live, so live carriers should reach ~100%.
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {infoBanner}
        <div className="flex-1 flex items-center justify-center"><div className="text-red-500">{error}</div></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {infoBanner}
      <div className="p-4 space-y-4">
        {stats && data && (
          <>
            {/* Progress toward 100%, broken down by category */}
            <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-heading mb-3">
                Progress toward 100% on gwiTrace
              </h3>
              <div className="space-y-3">
                {(data.category_progress ?? []).map((c) => {
                  const deferredPct = c.deferred_pct ?? 0;
                  const gwWidth = Math.min(Math.max(c.pct, c.pct > 0 ? 0.5 : 0), 100);
                  const defWidth = Math.min(deferredPct, Math.max(100 - gwWidth, 0));
                  return (
                    <div key={c.category}>
                      <div className="flex items-baseline justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 dark:text-gray-200">{c.category}</span>
                        <span className="tabular-nums text-muted">
                          <span className="font-semibold" style={{ color: ADOPTION_COLOR }}>{c.pct.toFixed(1)}%</span>
                          {' · '}{nfmt(c.on_gwitrace)} / {nfmt(c.trackable)}
                          {deferredPct > 0 && <span className="text-gray-400"> · {deferredPct.toFixed(1)}% deferred</span>}
                        </span>
                      </div>
                      <div className="h-5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex">
                        <div
                          className="h-full transition-all duration-500"
                          style={{ width: `${gwWidth}%`, backgroundColor: ADOPTION_COLOR }}
                          title={`On gwiTrace: ${c.pct.toFixed(1)}%`}
                        />
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${defWidth}%`,
                            backgroundImage:
                              'repeating-linear-gradient(45deg, #c3c8d0 0, #c3c8d0 5px, #e3e6ea 5px, #e3e6ea 10px)',
                          }}
                          title={`Deferred (in-house tracking): ${deferredPct.toFixed(1)}%`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 text-xs text-muted mt-3 flex-wrap">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: ADOPTION_COLOR }} /> On gwiTrace
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c3c8d0 0, #c3c8d0 3px, #e3e6ea 3px, #e3e6ea 6px)' }} /> Deferred (in-house tracking)
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm bg-gray-200 dark:bg-gray-700" /> Not yet tracked
                </span>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Share of each category's open trackable containers (on its planned carriers) that are on gwiTrace —
                goal 100%, minus the deferred in-house share. Independent lenses: a container can fall under both its
                ocean line and its rail/terminal, so the bars don't sum to one total.
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <StatCard
                label="Tracers Live"
                value={`${data.tracers_live} / ${data.tracers_total}`}
                accent={ADOPTION_COLOR}
              />
              <StatCard
                label="On gwiTrace"
                value={`${stats.reach.toFixed(1)}%`}
                sub={`${nfmt(stats.on_gwitrace)} of ${nfmt(stats.trackable)} open trackable`}
                accent={ADOPTION_COLOR}
              />
              <StatCard
                label="Total on gwiTrace (cumulative)"
                value={nfmt(stats.cumulativeTotal)}
                accent={ADOPTION_COLOR}
              />
            </div>
          </>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64 text-muted">Loading…</div>
        ) : data ? (
          <>
            {/* Rollout tracker */}
            <div>
              <h3 className="text-base font-semibold text-heading mb-1">
                Rollout Progress by Tracer
              </h3>
              <p className="text-xs text-muted mb-3">
                Each number is the carrier's currently <strong>open, trackable</strong> containers — those eligible for
                gwiTrace right now: they have a container # and BOL and haven't been outgated or trace-terminated.
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                {groupedRollout.map((g) => (
                  <div key={g.category} className="bg-panel rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-blue-100 dark:bg-blue-900/40 border-b border-blue-200 dark:border-blue-800 px-4 py-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-blue-800 dark:text-blue-200">
                        {g.category}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {g.rows.map((r) => (
                        <div key={r.name} className="px-4 py-2.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-1.5">
                              <span className="font-medium text-heading">{r.name}</span>
                              <span className="text-gray-400 dark:text-gray-500">–</span>
                              <span className="text-lg font-semibold tabular-nums text-heading">
                                {nfmt(r.trackable)}
                              </span>
                            </div>
                            {r.category === 'Terminal' && r.codes.length > 1 && (
                              <div className="text-xs text-muted mt-0.5">{r.covers}</div>
                            )}
                          </div>
                          <div className="shrink-0 text-right">
                            <StatusBadge status={r.status} />
                            {r.go_live && (
                              <div className="text-xs text-muted mt-0.5 tabular-nums whitespace-nowrap">
                                {fmtShort(parseDate(r.go_live)) + "/" + parseDate(r.go_live).getFullYear().toString().slice(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Overall reach trend */}
            <div className="bg-panel rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-base font-semibold text-heading">
                  Import Containers on gwiTrace Over Time (cumulative)
                </h3>
                <div className="inline-flex rounded-md shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {granularityOptions.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setGranularity(opt.key)}
                      className={`px-3 py-1 text-sm font-medium transition-colors ${
                        granularity === opt.key
                          ? 'bg-blue-600 text-white'
                          : 'bg-panel text-gray-600 dark:text-gray-300 hover:bg-highlight'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="label" tick={{ fill: 'currentColor', fontSize: 12 }} className="text-gray-600 dark:text-gray-400" />
                  <YAxis domain={[0, 'auto']} allowDecimals={false} tick={{ fill: 'currentColor', fontSize: 12 }} className="text-gray-600 dark:text-gray-400" />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'rgb(31 41 55)', border: 'none', borderRadius: '0.5rem', color: 'white' }}
                    formatter={((value: number) => [nfmt(value), 'containers on gwiTrace']) as any}
                  />
                  <Line type="monotone" dataKey="cumulative" name="containers on gwiTrace" stroke={ADOPTION_COLOR} strokeWidth={2.5} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default GwitraceProgress;
